/* AnalyticsAndLogs.tsx generated by @compiled/babel-plugin v0.36.0 */
/* eslint-disable max-lines-per-function */
import { ax, ix, CC, CS } from "@compiled/react/runtime";
import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import { Box, xcss } from '@atlaskit/primitives';
import { AccordionCollapsedEvent, AccordionExpandedEvent, ANALYTICS_ACTION_CLICKED, ANALYTICS_ACTION_COLLAPSED, ANALYTICS_ACTION_EXPANDED, ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { AccordionItemTriggerContent, AccordionV2, BodySmall } from '@atlassian/mpac-primitives';
import { ExternalLink } from '@atlassian/mpac-ui';
import { AccordionError } from '../AccordionError/AccordionError';
import { AccordionLoading } from '../AccordionLoading/AccordionLoading';
import { formatEgressAddress, IN_SCOPE_EUD_CHANGES } from '../analyticsAndEgressUtil';
import { ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADMINHUB_LINK, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ANALYTICS_ACCORDION } from '../events';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const _ = "._syaz1gjq{color:var(--ds-text-subtle,#44546f)}";
/* content utils and constants */
const getNonRoaDeclaration = () => /*#__PURE__*/_jsx(_Fragment, {
  children: "The information in this section only applies to components hosted on Atlassian. Other parts of the app may still send analytics data outside of Atlassian."
});
const getCommonAdminhubSettingsText = (link, onAdminhubLinkClick) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [' ', "You can change this setting at", ' ', /*#__PURE__*/_jsx(ExternalLink, {
    href: link,
    onClick: onAdminhubLinkClick,
    "aria-label": "Navigate to admin.atlassian.com",
    children: "admin.atlassian.com"
  }), ' ', "before installing the app or at any time after installing."]
});
const ENABLED_SETTINGS_TEXT = 'This app will send data,';
const DISABLED_SETTINGS_TEXT = 'If enabled, this app will send data,';
const getInScopeEudValue = inScopeEUDForLatestVersion => {
  if (inScopeEUDForLatestVersion === IN_SCOPE_EUD_CHANGES.EXCLUDED) {
    return 'excluding';
  }
  return 'including';
};
const subtleTextStyle = null;
const analyticsEgressStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  margin: 'space.0',
  gap: 'space.100'
});
export const AnalyticsAndLogs = _ref => {
  let {
    analyticsEgress,
    links,
    isGlobalLogsAndAnalyticsEnabled,
    analyticsQueryError,
    installConfigQueryError,
    inScopeEUDForLatestVersion,
    roaStatus,
    isForgeApp,
    isLoading,
    cloudId
  } = _ref;
  const fireAnalyticsEvent = useAnalyticsEvents();
  const handleAdminhubLinkClick = () => {
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      event: ANALYTICS_ACTION_CLICKED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADMINHUB_LINK,
      attributes: {
        section: 'Analytics and Logs'
      }
    });
  };
  const getGlobalControlsText = () => {
    const adminhubLink = links.adminHub.expand({});
    const directLinkToSettings = cloudId ? "".concat(adminhubLink, "/s/").concat(cloudId, "/user-connected-apps/tab/settings") : adminhubLink;
    const commonSettingText = getCommonAdminhubSettingsText(directLinkToSettings, handleAdminhubLinkClick);
    if (installConfigQueryError) {
      return /*#__PURE__*/_jsx(AccordionError, {
        label: "Analytics install config error"
      });
    }
    /* ----- ENABLED ----- */
    if (isGlobalLogsAndAnalyticsEnabled) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: ["Sending of analytics and sharing of logs are currently enabled.", commonSettingText]
      });
    }
    /* ----- DISABLED -----
       append non-roa declaration if analytics is disabled and analytics data is not there
       need to check analyticsEgress.length > 0 as otherwise same declaration will be inside accordion
    */
    const showAnalyticsDisabledTextForNonRoa = analyticsEgress.length === 0 && roaStatus !== true;
    const analyticsDisabledTextForNonRoa = showAnalyticsDisabledTextForNonRoa ? 'This setting only applies to app components hosted on Atlassian. Other parts of the app may still send analytics and share logs outside of Atlassian.' : '';
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: ["Sending of analytics and sharing of logs are currently disabled.", ' ', analyticsDisabledTextForNonRoa, commonSettingText]
    });
  };
  const handleAnalyticsAccordionItemExpand = () => {
    fireAnalyticsEvent({
      eventType: AccordionExpandedEvent,
      event: ANALYTICS_ACTION_EXPANDED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ANALYTICS_ACCORDION
    });
  };
  const handleAnalyticsAccordionItemCollapse = () => {
    fireAnalyticsEvent({
      eventType: AccordionCollapsedEvent,
      event: ANALYTICS_ACTION_COLLAPSED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ANALYTICS_ACCORDION
    });
  };
  const renderAccordionItems = () => {
    if (!isForgeApp) {
      return [];
    }
    return analyticsEgress.length > 0 || analyticsQueryError ? [{
      heading: () => /*#__PURE__*/_jsx(AccordionItemTriggerContent, {
        title: "Analytics",
        icon: /*#__PURE__*/_jsx(ChartBarIcon, {
          label: "",
          color: "var(--ds-icon-disabled, #091E424F)"
        })
      }),
      content: renderAccordionContent,
      key: 'analytics-and-logs',
      ariaLabel: 'Analytics details'
    }] : [];
  };
  if (isLoading) {
    return /*#__PURE__*/_jsx(AccordionLoading, {});
  }
  const renderAccordionContent = () => {
    if (analyticsQueryError) {
      return /*#__PURE__*/_jsx(AccordionError, {
        label: "Analytics and logs error"
      });
    }
    const ulDescriptionInitialText = isGlobalLogsAndAnalyticsEnabled ? ENABLED_SETTINGS_TEXT : DISABLED_SETTINGS_TEXT;
    const ulFullDescription = "".concat(ulDescriptionInitialText, " ").concat(getInScopeEudValue(inScopeEUDForLatestVersion), " in-scope End-User Data, to domain(s) outside of Atlassian:");
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(CC, {
        children: [/*#__PURE__*/_jsx(CS, {
          children: [_]
        }), /*#__PURE__*/_jsx(BodySmall, {
          className: ax(["_syaz1gjq"]),
          children: ulFullDescription
        })]
      }), /*#__PURE__*/_jsx(Box, {
        as: "ul",
        paddingBlockStart: "space.100",
        paddingInlineStart: "space.200",
        paddingInlineEnd: "space.600",
        xcss: analyticsEgressStyles,
        children: formatEgressAddress(analyticsEgress).map(analyticsUrl => /*#__PURE__*/_jsx(Box, {
          as: "li",
          children: analyticsUrl
        }, analyticsUrl))
      }), roaStatus !== true ? /*#__PURE__*/_jsxs(CC, {
        children: [/*#__PURE__*/_jsx(CS, {
          children: [_]
        }), /*#__PURE__*/_jsx(BodySmall, {
          className: ax(["_syaz1gjq"]),
          children: getNonRoaDeclaration()
        })]
      }) : null]
    });
  };
  return /*#__PURE__*/_jsx(AccordionV2, {
    mainTitle: "Analytics and logs",
    onExpandItem: handleAnalyticsAccordionItemExpand,
    onCollapseItem: handleAnalyticsAccordionItemCollapse,
    description: !isForgeApp ? /*#__PURE__*/_jsxs(CC, {
      children: [/*#__PURE__*/_jsx(CS, {
        children: [_]
      }), /*#__PURE__*/_jsx(BodySmall, {
        className: ax(["_syaz1gjq"]),
        children: "This app may send analytics and share logs outside of Atlassian."
      })]
    }) : /*#__PURE__*/_jsxs(CC, {
      children: [/*#__PURE__*/_jsx(CS, {
        children: [_]
      }), /*#__PURE__*/_jsx(BodySmall, {
        className: ax(["_syaz1gjq"]),
        children: getGlobalControlsText()
      })]
    }),
    items: renderAccordionItems()
  });
};